import { FiniteStateMachine, type ActionFn } from 'runed'
import { untrack } from 'svelte'

import { appState } from './appstate.svelte'
// FIXME: can we reuse the websocket? Do we need a separate instance?
// should we bind the websocket in appState and recycle from there?
import { webSocket } from '#lib/websocket.svelte.js'
import { type MapMarker } from '$mapapp/MapApp/MapView/Marker/BasicMarker.model'
import type {
	LotMatchPartner,
	MatchOverviewData,
	PlatformRequestMailInfo,
	PopMatchPartner,
	ReturnFreightData
} from '../data/data.model'
import { SvelteMap, SvelteSet } from 'svelte/reactivity'
import { fetchUtils } from '$utils/fetchUtils'
import type { MiniLot } from '$mapapp/types/miniLot'
import { DEFAULT_RADIUS_KM } from './matching-config'
import { MatchingResultsState } from '$mapapp/MapApp/SlideIn/Views/Matching/MatchingResults/matchingResultsState.svelte'
import {
	toMarkerId,
	type MatchingOptions,
	type MatchingSetEvents,
	type States
} from './matching/model'
import { frameRadius, frameRoute, redrawPendingRoute } from './matching/route'
import { applySelection, AutoSelection, selectMatch } from './matching/selection'
import {
	closeMatchStream,
	fetchReference,
	openMatchStream,
	requestMatches,
	requestMatchesForMiniLot
} from './matching/stream'
import {
	clearMirroredSelection,
	enterSecondaryView,
	goBackFromMatching
} from './matching/navigation'

/** re-exported so the four consumers that import it alongside `Matching` keep one import line */
export type { MatchingOptions } from './matching/model'

export class Matching {
	readonly #autoSelect = new AutoSelection(this)

	readonly #selectMatch = (...args: unknown[]): States | void =>
		selectMatch(this, args, this.#autoSelect)

	applyAutoSelection() {
		this.#autoSelect.apply()
	}

	/**
	 * Match might get selected before the partner marker arrives in {@link appState.allMarkers}.
	 * Kept here so a follower (e.g. LayerMatching's reactive effect on `matchMarkers`)
	 * can retry the route fetch once the partner streams in.
	 */
	pendingRouteMatch: MatchOverviewData | null = $state(null)

	/** Controls visibility of the RouteInfoOverlay on the map. Opened only by clicking the route polyline. */
	routingInfoVisible: boolean = $state(false)

	recenterRoute(secondaryWidthPx?: number): void {
		frameRoute(secondaryWidthPx)
	}

	async fitToRadius(): Promise<boolean> {
		return frameRadius(this)
	}

	retryPendingRoute(): void {
		redrawPendingRoute(this)
	}

	readonly #set_secondary = (...args: unknown[]): States | void => enterSecondaryView(this, args)

	fsm = new FiniteStateMachine<States, MatchingSetEvents>('results', {
		results: {
			set_result: this.#selectMatch,
			set_secondary: this.#set_secondary as ActionFn<States>
		},
		result: {
			set_result: this.#selectMatch,
			set_results: () => {
				appState.route.clearCoordinates()
				return 'results'
			},
			set_secondary: this.#set_secondary as ActionFn<States>
		},
		secondary: {
			_exit: () => {
				clearMirroredSelection(this)
				appState.closePopup()
			},
			set_result: this.#selectMatch,
			set_results: () => {
				appState.route.clearCoordinates()
				return 'results'
			},
			set_secondary: this.#set_secondary as ActionFn<States>
		}
	})

	referenceId: string | undefined = $state(undefined)
	referenceMarker: MapMarker | undefined = $state(undefined)
	selectedMarkerType: 'pop' | 'lot' | 'miniLot' | null = $state(null)
	profileId: string | undefined = $state(undefined) // Optional: specific profile ID for Pop matching

	/** Distance range (km) for matching. Used by MatchingTable slider and set_matching payload. */
	radius: { from: number; to: number } = $state({ from: 0, to: DEFAULT_RADIUS_KM })
	/** Last radius committed via refetch(). Slider-only edits leave this unchanged so the radius
	 *  layer can render a pending-preview (dashed orange) until the user confirms. */
	confirmedRadius: { from: number; to: number } = $state({ from: 0, to: DEFAULT_RADIUS_KM })
	/** Sticky flag: user has touched the slider since the last refetch(). Stays true even if the
	 *  slider snaps back to a value equal to confirmedRadius — prevents the preview from flashing. */
	isRadiusPending: boolean = $state(false)

	markRadiusEdited() {
		this.isRadiusPending = true
	}

	matchReferenceData: LotMatchPartner | PopMatchPartner | undefined = $state(undefined)
	readonly #cleanupReferenceData: (() => void) | undefined // binding for destroying effect root
	#destroyed = false

	// Matches from API (!= markers). key shape: see toMarkerId above
	matches: SvelteMap<string, MatchOverviewData> = $state(new SvelteMap())
	matchMarkerIds: SvelteSet<string> = $derived.by(
		() =>
			new SvelteSet(
				Array.from(this.matches.values(), (match) =>
					toMarkerId(match.partner.objType, match.partner.objId)
				)
			)
	)

	/**
	 * markers this session is waiting on, populated via receiveMarker() or the constructor's
	 * seeding effect. matchMarkers/returnFreightMarkers read from here instead of
	 * appState.allMarkers: that map's `.get()` falls back to its whole-map version signal for a
	 * key that doesn't exist yet, so a consumer waiting on one marker would get invalidated by
	 * every unrelated marker arriving anywhere in the app. This map only changes for markers this
	 * session actually asked for.
	 */
	receivedMarkersById: SvelteMap<string, MapMarker> = new SvelteMap()

	// called by appstate.svelte.ts for every marker streamed in over the websocket — pushes it in
	// the instant it arrives, so matchMarkers/returnFreightMarkers don't have to poll for it
	receiveMarker(marker: MapMarker) {
		if (
			this.matchMarkerIds.has(marker.markerId) ||
			this.#returnFreightMarkerIds.has(marker.markerId)
		) {
			this.receivedMarkersById.set(marker.markerId, marker)
		}
	}

	// TODO Check if relevant: array of markers, because we need to keep the order of matches
	matchMarkers: (MapMarker | undefined)[] = $derived.by(() =>
		Array.from(this.matches.values(), (match) =>
			this.receivedMarkersById.get(toMarkerId(match.partner.objType, match.partner.objId))
		)
	)
	matchesLoading: boolean = $derived(webSocket.isStreamBusy('/return/matches'))

	/** Shared ranking + active-tab state for the results list and the map markers. */
	resultsState = new MatchingResultsState(
		() => this.matches,
		() => this.matchReferenceData
	)

	returnFreights: SvelteMap<string, ReturnFreightData> = $state(new SvelteMap())
	readonly #returnFreightMarkerIds: SvelteSet<string> = $derived.by(
		() => new SvelteSet(Array.from(this.returnFreights.values(), (rf) => `lot-${rf.id}`))
	)
	returnFreightMarkers: (MapMarker | undefined)[] = $derived.by(() =>
		Array.from(this.returnFreights.values(), (returnFreight) =>
			this.receivedMarkersById.get(`lot-${returnFreight.id}`)
		)
	)

	selectedMatch: MatchOverviewData | undefined = $state(undefined)
	/**
	 * trails {@link selectedMatch} by the length of the crossfade, so the outgoing card keeps its
	 * place in the top slot while the incoming one flies in over it. both slide-ins render from this
	 * field, so neither can show a match the other one does not have yet.
	 */
	displayedMatch: MatchOverviewData | undefined = $state(undefined)
	/**
	 * this flag gates {@link applyAutoSelection}. it is reset to true on every fresh match stream,
	 * which means in the constructor and in refetch, and it is flipped to false in
	 * dismissSelectedMatch so that the dismissal by the user is not immediately undone by a
	 * re-selection of the first row.
	 */
	autoSelectFirst: boolean = $state(true)
	selectedMatchId: string | undefined = $derived.by(() =>
		this.selectedMatch
			? toMarkerId(this.selectedMatch.partner.objType, this.selectedMatch.partner.objId)
			: undefined
	)

	miniLot: MiniLot | undefined = $state(undefined)

	/**
	 * per-session memo for the platform-request mail infos. a `SvelteMap` is already reactive, so it
	 * needs no `$state` wrapper. four of the five callers pass `invalidateCache: true`, so in practice
	 * this only spares a second fetch inside one open panel.
	 */
	readonly #partnerMailInfoCache: SvelteMap<string, Promise<PlatformRequestMailInfo>> =
		new SvelteMap()
	requestId: string = Date.now().toString()

	/** the secondary view's mirrored marker selection, written by `matching/navigation.ts` */
	// TODO possibly refactor as selectedMarker, then we can give all information to the slide in, the checks can happen via selectedMarker.id
	temp_selectedId: string | null = $state(null)
	temp_selectedType: MapMarker['type'] | 'cluster' | undefined = $state(undefined)
	temp_selectedChildrenIds: string[] = $state([])

	/**
	 * HINT: keep constructor narrow; ideally we just need an id and an options object
	 * currently, we have mixed id semantics (compound id type-id vs. split id type/id)
	 * we need additional values depending on special cases, such as matching from a profile or
	 * a different search radius
	 * Hence: options object for easier extentability of the constructor interface
	 * @example:
	 * - plain: new Matching(markerId, markerType)
	 * - with profileId: new Matching(markerId, markerType, { profileId: '123' })
	 * - with radius: new Matching(markerId, markerType, { radius: { from: 0, to: 1000 } })
	 * - with profileId and radius: new Matching(markerId, markerType, { profileId: '123', radius: { from: 0, to: 1000 } })
	 */
	// TODO: Document Minilot stuff
	constructor(
		markerType: 'pop' | 'lot' | 'miniLot',
		markerId?: string,
		options: MatchingOptions = {}
	) {
		this.referenceId = markerId
		if (markerType !== 'miniLot') {
			// miniLot has no pre-resolved marker; subscribeMiniLotCoordinates
			// assigns referenceMarker after server returns coords
			this.referenceMarker = appState.selectedMarker
		}
		this.selectedMarkerType = markerType

		// FIXME: Only if there is a profile id, we can start the matching for markerType 'pop'. We do not yet allow matching
		// on all profiles of a pop at once.
		if (options?.profileId) {
			// TODO: make sure this is cleaned up when switching / resetting
			this.profileId = options.profileId
		}

		if (options?.radius) {
			this.radius = { from: options.radius.from, to: options.radius.to }
		}
		this.confirmedRadius = { from: this.radius.from, to: this.radius.to }

		if (options?.miniLot) {
			this.miniLot = options.miniLot
		}

		// HINT: we need to fetch the data for the reference marker with an abort signal
		// we did not want to rewrite the getReferenceMarkerData function to provide our own abort signal
		// Hence we're using a trick here
		// LEAVE it like this until we / Svelte provides a better solution for async in Svelte 5 classes
		this.#cleanupReferenceData = $effect.root(() => {
			$effect(() => {
				void fetchReference(markerId, markerType, this.miniLot).then((data) => {
					if (this.#destroyed) return
					this.matchReferenceData = data
					// without a reference `#totalCost` ties every match at 0, so this re-sorts the list
					this.#autoSelect.noteRankingData()
					this.#autoSelect.apply()
				})
			})

			/**
			 * catches markers that streamed in before their match/return-freight was added —
			 * receiveMarker only pushes arrivals after this session starts listening. the lookup
			 * and write below run in `untrack` on purpose: reading receivedMarkersById.has() for a
			 * still-missing id and then writing to that same map in the same effect would make the
			 * effect depend on its own write (self-retrigger), and reading allMarkers.get() for a
			 * still-missing id would resubscribe this effect to allMarkers' coarse version — the
			 * exact over-invalidation this map exists to avoid. matchMarkerIds/
			 * #returnFreightMarkerIds are the only intended triggers.
			 *
			 * AGENTS.md:116 forbids writing reactive state from an `$effect`, and this is the one
			 * place that does it. the alternative — falling back to `appState.allMarkers.get(id)`
			 * inside `matchMarkers` — reintroduces that coarse subscription for every id the stream
			 * has not resolved yet, which is the regression LayerMatching's deleted debounce was
			 * working around.
			 */
			$effect(() => {
				const matchIds = this.matchMarkerIds
				const returnFreightIds = this.#returnFreightMarkerIds
				untrack(() => {
					for (const id of matchIds) {
						if (this.receivedMarkersById.has(id)) continue
						const marker = appState.allMarkers.get(id)
						if (marker) this.receivedMarkersById.set(id, marker)
					}
					for (const id of returnFreightIds) {
						if (this.receivedMarkersById.has(id)) continue
						const marker = appState.allMarkers.get(id)
						if (marker) this.receivedMarkersById.set(id, marker)
					}
				})
			})
		})

		openMatchStream(this, this.#autoSelect)
	}

	setNextMatch() {
		if (!appState.isMatching || !this.selectedMatch) {
			return
		}

		const key = toMarkerId(this.selectedMatch.partner.objType, this.selectedMatch.partner.objId)
		const keys = [...this.matches.keys()]
		const currentIndex = keys.indexOf(key)

		if (currentIndex === -1) return

		const nextKey = keys[(currentIndex + 1) % keys.length] // wraps around
		const nextMatch = this.matches.get(nextKey)

		if (nextMatch) {
			this.fsm.send('set_result', { id: nextMatch.partner.objId, type: nextMatch.partner.objType })
		}
	}

	hasNextMatch() {
		if (!appState.isMatching || !this.selectedMatch || this.matches.size < 2) return false

		const key = toMarkerId(this.selectedMatch.partner.objType, this.selectedMatch.partner.objId)
		const keys = [...this.matches.keys()]
		const currentIndex = keys.indexOf(key)

		return currentIndex !== -1 && currentIndex + 1 < keys.length
	}

	async getPartnerMailInfo(
		popId: string,
		invalidateCache: boolean = false
	): Promise<PlatformRequestMailInfo | undefined> {
		const cached = this.#partnerMailInfoCache.get(popId)
		if (cached && !invalidateCache) {
			return cached
		}

		const refId = this.matchReferenceData?.objId
		if (!refId || refId === 'minilot') {
			return undefined
		}

		const request = fetchUtils
			.get(`/api/platformRequest/lastMailInfos/${refId}/${popId}`, false)
			.catch((err) => {
				console.error('failed to fetch mail infos', err.message)
			})

		this.#partnerMailInfoCache.set(popId, request)
		return request
	}

	handleMatchingHistory() {
		goBackFromMatching(this, this.#autoSelect)
	}

	setReturnFreights(lotIds: string[]) {
		this.returnFreights.clear()
		lotIds.forEach((lotId) => {
			this.returnFreights.set(toMarkerId('lot', lotId), { id: lotId } as ReturnFreightData)
		})
	}

	clearReturnFreights() {
		this.returnFreights.clear()
	}

	/** commits the radius the slider is showing and starts a second stream on this session */
	refetch() {
		closeMatchStream(this)
		this.confirmedRadius = { from: this.radius.from, to: this.radius.to }
		this.isRadiusPending = false
		this.autoSelectFirst = true
		/**
		 * a re-fetch is the only path that starts a second stream on a running session, so the
		 * auto-selection's per-stream record has to be dropped here. the new fetch also invalidates
		 * the old match set, and clearing the selection keeps the route polyline off a partner that
		 * is no longer in the result set.
		 */
		this.#autoSelect.reset()
		applySelection(this, undefined, this.#autoSelect)
		this.receivedMarkersById.clear()
		appState.route.clearCoordinates()
		if (this.fsm.current === 'result') {
			this.fsm.send('set_results')
		}
		if (this.miniLot) {
			requestMatchesForMiniLot(this)
		} else {
			requestMatches(this)
		}
		void frameRadius(this)
	}

	destroy() {
		this.#destroyed = true
		this.#cleanupReferenceData?.()
		this.#autoSelect.destroy()
		this.receivedMarkersById.clear()
		this.#partnerMailInfoCache.clear()
		webSocket.unsubscribe('/return/matches')
		webSocket.unsubscribe('/return/minilot/coordinates')
		webSocket.unsubscribe('/return/minilot/materialProperties')
		// abort server-side workers and release local registry so busyStreams
		// drops immediately instead of waiting for the 30s failsafe
		closeMatchStream(this)
		appState.route.clearCoordinates()
	}
}
